/**
 * The admin screen's decision-making, kept out of the component.
 *
 * Same convention as the rest of src/ui/state: anything that decides an outcome
 * — whether a phone number is sendable, what a failure means in English, which
 * surveyors may be assigned work — is a pure function here, so it is tested in
 * the node environment the unit suite runs in rather than asserted against a
 * rendered DOM. The `.tsx` file stays dumb on purpose.
 *
 * Nothing in this module knows what a surveyor token is. It cannot format one,
 * validate one or carry one; the office works in names (D-005).
 */

import { formatAddress } from '../../core/address';
import type { OfficeSurveySummary, SurveyorSummary } from '../../db/admin-client';

/* ──────────────────────────────── phone ──────────────────────────────────── */

/** E.164, matching the API's own regex in functions/api/[[route]].ts. */
const E164 = /^\+[1-9]\d{7,14}$/;

/** One text field's verdict: the cleaned value, or why it was refused. */
export type FieldResult = { ok: true; value: string } | { ok: false; message: string };

/**
 * Turn what the office actually types into what Twilio actually accepts.
 *
 * Chip types "(555) 123-4567". The API rejects anything but `+15551234567`, and
 * a 400 from a phone field is a bad way to learn about E.164 — so the formatting
 * is fixed here and only genuinely ambiguous input is refused. A bare 10-digit
 * number is assumed to be +1: OSB works one country, and the alternative is
 * asking a dispatcher to type a country code they have never needed.
 */
export function normalizePhone(raw: string): FieldResult {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, message: 'Enter a mobile number — the assignment text has nowhere else to go.' };
  }

  const compact = trimmed.replace(/[\s().-]/g, '');
  const digits = compact.replace(/\D/g, '');

  let candidate: string;
  if (compact.startsWith('+')) candidate = `+${digits}`;
  else if (digits.length === 10) candidate = `+1${digits}`;
  else if (digits.length === 11 && digits.startsWith('1')) candidate = `+${digits}`;
  else candidate = '';

  if (!candidate || !E164.test(candidate)) {
    return {
      ok: false,
      message: 'That does not look like a mobile number. Try 555 123 4567, or +1 555 123 4567.',
    };
  }
  return { ok: true, value: candidate };
}

/** How a number is shown back in the roster: +15551234567 → +1 555 123 4567. */
export function formatPhone(phone: string | undefined): string {
  if (!phone) return 'No mobile number';
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone);
  return match ? `+1 ${match[1]} ${match[2]} ${match[3]}` : phone;
}

export function validateName(raw: string): FieldResult {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length === 0) return { ok: false, message: 'Enter the surveyor’s name.' };
  if (name.length > 120) return { ok: false, message: 'That name is too long (120 characters max).' };
  return { ok: true, value: name };
}

/* ──────────────────────────────── roster ─────────────────────────────────── */

export interface RosterRow {
  surveyor: SurveyorSummary;
  /** "Active" / "Deactivated" — the tag on the row. */
  statusLabel: string;
  statusTone: 'active' | 'inactive';
  phoneLabel: string;
  lastSeenLabel: string;
  /** False when they are inactive or have no number: assignment would fail. */
  assignable: boolean;
  /** Why not, when `assignable` is false. Empty otherwise. */
  blockedReason: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "When did this surveyor last use the app?"
 *
 * NOT "last assigned" — the registry does not record that, and `GET /api/surveys`
 * is scoped to the calling surveyor so the office cannot derive it either. Saying
 * "last opened" is the honest version of the fact that is actually available;
 * inventing "last assigned" from `lastSeenAt` would put a wrong date in front of
 * a dispatcher deciding who is free.
 */
export function describeLastSeen(lastSeenAt: string | undefined, nowIso: string): string {
  if (!lastSeenAt) return 'Has not opened the app yet';

  const then = Date.parse(lastSeenAt);
  const now = Date.parse(nowIso);
  if (Number.isNaN(then) || Number.isNaN(now)) return 'Has not opened the app yet';

  const days = Math.floor((startOfUtcDay(now) - startOfUtcDay(then)) / 86_400_000);
  if (days <= 0) return 'Opened the app today';
  if (days === 1) return 'Opened the app yesterday';
  if (days < 7) return `Opened the app ${String(days)} days ago`;

  const date = new Date(then);
  return `Opened the app ${String(date.getUTCDate())} ${MONTHS[date.getUTCMonth()] ?? ''}`.trim();
}

function startOfUtcDay(ms: number): number {
  return Math.floor(ms / 86_400_000) * 86_400_000;
}

export function toRosterRow(surveyor: SurveyorSummary, nowIso: string): RosterRow {
  const blockedReason = !surveyor.active
    ? 'Deactivated — their link no longer works.'
    : surveyor.phone
      ? ''
      : 'No mobile number, so there is nowhere to text a link.';

  return {
    surveyor,
    statusLabel: surveyor.active ? 'Active' : 'Deactivated',
    statusTone: surveyor.active ? 'active' : 'inactive',
    phoneLabel: formatPhone(surveyor.phone),
    lastSeenLabel: describeLastSeen(surveyor.lastSeenAt, nowIso),
    assignable: surveyor.active && Boolean(surveyor.phone),
    blockedReason,
  };
}

/** Active first, then by name — the same order the API's own query uses. */
export function buildRoster(surveyors: SurveyorSummary[], nowIso: string): RosterRow[] {
  return [...surveyors]
    .sort((a, b) =>
      a.active === b.active ? a.name.localeCompare(b.name) : a.active ? -1 : 1,
    )
    .map((surveyor) => toRosterRow(surveyor, nowIso));
}

/** The names that may be picked in the assign form — never ids, never tokens. */
export function assignableSurveyors(rows: RosterRow[]): RosterRow[] {
  return rows.filter((row) => row.assignable);
}

/* ────────────────────────── the office's records ─────────────────────────── */

export interface OfficeSurveyRow {
  survey: OfficeSurveySummary;
  /** Building name, else address, else a placeholder — never a bare id. */
  title: string;
  subtitle: string;
  statusLabel: string;
  tagKind: 'assigned' | 'field' | 'submitted';
  /** True when there is still something to remove. */
  removable: boolean;
  /**
   * True when this survey can be SENT BACK to the surveyor (D-018): submitted,
   * and still in the records. A live draft has nothing to recall — it is already
   * with the surveyor — and a removed record is not resurrected, it is replaced
   * by a fresh assignment.
   */
  recallable: boolean;
  /** Why it cannot be removed again, and by whom it was. Empty when live. */
  removedNote: string;
  /** "Sent back to the surveyor on …", or empty when it never was. */
  recalledNote: string;
  /**
   * "Finished on the phone …, N photos still on the way", or empty (D-052
   * §3). Present only beside a NON-submitted, live status; the label above
   * it then reads *Submitted — arriving* rather than *In progress*.
   */
  arrivingNote: string;
  /** Whether removing this one takes out a SUBMITTED record. Changes the copy. */
  submitted: boolean;
}

const OFFICE_STATUS_LABEL: Record<OfficeSurveySummary['status'], string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  submitted_clean: 'Submitted — clean',
  submitted_with_gaps: 'Submitted — with gaps',
};

/**
 * One row of the office's survey list (D-012).
 *
 * A withdrawn survey is SHOWN, not hidden, and says who withdrew it. "Dave
 * discarded it" and "it was never assigned" are different facts, and the office
 * is the only principal that can see the difference at all — the surveyor's own
 * list stops listing it the moment it is discarded.
 */
export function toOfficeSurveyRow(survey: OfficeSurveySummary): OfficeSurveyRow {
  const submitted =
    survey.status === 'submitted_clean' || survey.status === 'submitted_with_gaps';
  const discarded = Boolean(survey.discardedAt);

  const removedNote = !discarded
    ? ''
    : survey.discardedReason === 'admin_deleted'
      ? 'Removed from the office. The record itself is kept.'
      : 'Withdrawn by the surveyor. The record itself is kept.';

  // A recall is not a black mark and must not read as one — it is the office
  // asking for more, and the surveyor is already working it again. Stated as a
  // fact with a date, in the same voice as everything else on the row.
  const recalledNote = !survey.recalledAt
    ? ''
    : submitted
      ? `Sent back to the surveyor on ${officeDay(survey.recalledAt)}, and submitted again since.`
      : `Sent back to the surveyor on ${officeDay(survey.recalledAt)}. It is out being worked.`;

  // D-052 §3 — Grand Dunes read *In progress* all night for a survey Chip had
  // finished at 8:29 PM, because the seal waits behind the photos (D-050) and
  // the office had no word for the hours in between. The phone now sends that
  // word ahead: the surveyor is done, the pictures are on their way, and the
  // whole survey — PDF, notice, gaps — follows when the last one lands.
  const arriving = !submitted && !discarded && Boolean(survey.sealedOnDeviceAt);
  const photosOwed = survey.photosOwed ?? 0;
  const arrivingNote = !arriving
    ? ''
    : `Finished on the phone ${officeDay(survey.sealedOnDeviceAt ?? '')}. ` +
      `${photosOwed} photo${photosOwed === 1 ? '' : 's'} still on the way; ` +
      'the survey comes in whole when the last one lands.';

  // The one printed line (D-039): street, city, state zip, composed by the
  // same function the notice and the cut sheet use — so a row assigned with
  // the parts split reads like one typed whole, and an old row reads as it did.
  const address = formatAddress({
    street: survey.address,
    city: survey.city,
    state: survey.state,
    zip: survey.zip,
  });

  return {
    survey,
    title: survey.buildingName ?? (address || 'Untitled survey'),
    subtitle: survey.buildingName && address ? address : survey.id,
    statusLabel: arriving ? 'Submitted — arriving' : OFFICE_STATUS_LABEL[survey.status],
    tagKind: submitted ? 'submitted' : survey.origin === 'field_initiated' ? 'field' : 'assigned',
    removable: !discarded,
    recallable: submitted && !discarded,
    removedNote,
    recalledNote,
    arrivingNote,
    submitted,
  };
}

/**
 * "3 Aug". The office reads these on a desktop beside a phone call, so the day
 * is what matters; the exact minute is in the record. An unparseable timestamp
 * passes through rather than becoming "Invalid Date" on Chip's screen.
 */
function officeDay(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Live records first, then the removed ones. Newest first inside each block. */
export function buildSurveyRecords(surveys: OfficeSurveySummary[]): OfficeSurveyRow[] {
  return [...surveys]
    .map(toOfficeSurveyRow)
    .sort((a, b) => {
      if (a.removable !== b.removable) return a.removable ? -1 : 1;
      return a.survey.updatedAt < b.survey.updatedAt ? 1 : -1;
    });
}

/* ────────────────────────────── the view ─────────────────────────────────── */

/**
 * Which rows the office is looking at (D-048).
 *
 * "Current" is the working view: active surveyors and live surveys.
 * "Everything" adds the deactivated and the removed, each still labelled with
 * who did it — D-012's distinction between "removed" and "never existed" is
 * kept, one tap away instead of in the way. The page opens on Current: the
 * office reads this list to dispatch, and a removed record has nothing to
 * dispatch.
 */
export type OfficeView = 'current' | 'everything';

export const OFFICE_VIEW_OPTIONS: readonly { value: OfficeView; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'everything', label: 'Everything' },
];

export function rosterInView(rows: RosterRow[], view: OfficeView): RosterRow[] {
  return view === 'everything' ? rows : rows.filter((row) => row.surveyor.active);
}

export function recordsInView(rows: OfficeSurveyRow[], view: OfficeView): OfficeSurveyRow[] {
  return view === 'everything' ? rows : rows.filter((row) => !row.survey.discardedAt);
}

/**
 * The one line under a list that is holding something back. Empty when nothing
 * is: a "0 hidden" line would be noise on every fresh install.
 */
export function hiddenLine(hidden: number, what: 'surveyors' | 'surveys'): string {
  if (hidden <= 0) return '';
  const noun =
    what === 'surveyors'
      ? hidden === 1
        ? '1 deactivated surveyor is'
        : `${hidden} deactivated surveyors are`
      : hidden === 1
        ? '1 removed survey is'
        : `${hidden} removed surveys are`;
  return `${noun} not shown. Choose Everything to see ${hidden === 1 ? 'it' : 'them'}.`;
}

/* ─────────────────────────────── messages ────────────────────────────────── */

/**
 * Every failure the office can hit, in words a dispatcher can act on.
 *
 * The API's error codes are precise and unreadable (`surveyor_has_no_phone`,
 * `sms_not_configured`). Mapping them in one place means a new code shows up as
 * a generic-but-honest message rather than as a raw identifier on screen.
 */
export function adminErrorMessage(error: { code: string; status: number; message: string }): string {
  switch (error.code) {
    case 'offline':
      return 'No connection to the office server. The admin page needs one — nothing was changed.';
    case 'no_office_key':
      return 'Enter the office key to manage surveyors.';
    case 'unauthorized':
      return 'That office key was not accepted. Check it and try again.';
    case 'surveyor_exists':
      return 'There is already a surveyor with that id.';
    case 'surveyor_not_found':
      return 'That surveyor is no longer in the registry. Reload the page.';
    case 'surveyor_inactive':
      return 'That surveyor is deactivated. Reactivate them before assigning work.';
    case 'surveyor_has_no_phone':
      return 'That surveyor has no mobile number, so there is nowhere to text their link.';
    // The API no longer returns this: D-010 turned "no sender number" from a
    // refusal into a suppression. Kept because an OLDER deployed Worker still
    // answers with it, and a stale build must not put a raw code on screen.
    case 'sms_not_configured':
      return 'Texting is not switched on for this server yet. Nothing was assigned.';
    case 'survey_sealed':
      return 'That survey has already been submitted and cannot be reassigned.';
    case 'survey_discarded':
      return 'That survey was removed from the records. Assign a new one instead.';
    // D-018. Both of these mean the list on screen is out of date rather than
    // that anything went wrong — say what to do about it, not what the code was.
    case 'survey_not_submitted':
      return 'That survey has not been submitted, so there is nothing to send back. Reload the page.';
    case 'no_submitted_document':
      return 'The office holds no submitted copy of that survey, so it cannot be sent back.';
    case 'not_found':
      return 'That survey is no longer in the records. Reload the page.';
    case 'invalid_body':
      return error.message || 'The server would not accept those details.';
    case 'bad_response':
      return 'The server sent something unexpected. Try again.';
    default:
      return error.message || 'Something went wrong. Nothing was changed.';
  }
}

/** What the office is told after an assignment lands. */
export function assignmentMessage(outcome: {
  surveyorName: string;
  smsAccepted: boolean;
  smsError?: string;
  smsSuppressed?: boolean;
  surveyorLinked?: boolean;
}): { tone: 'ok' | 'warn'; text: string } {
  if (outcome.smsAccepted) {
    return {
      tone: 'ok',
      text: `Assigned to ${outcome.surveyorName}. Their link has been texted to them.`,
    };
  }

  // D-010: texting is switched off, so nothing was attempted and nothing failed.
  // Saying "the text did not send" here would send the office round to assign it
  // again, forever, for a message that is never going to be tried.
  if (outcome.smsSuppressed) {
    if (outcome.surveyorLinked === false) {
      return {
        tone: 'warn',
        text:
          `Assigned to ${outcome.surveyorName}, but they have no working link yet ` +
          'and texting is switched off, so the job cannot reach them. ' +
          'Get them set up on a device before this one can be worked.',
      };
    }
    return {
      tone: 'ok',
      text:
        `Assigned to ${outcome.surveyorName}. Texting is switched off, so no text ` +
        'was sent — the job appears in their app the next time it syncs.',
    };
  }
  // The survey IS assigned; only the text failed. Saying "failed" outright would
  // send the office round again and assign it twice.
  return {
    tone: 'warn',
    text:
      `Assigned to ${outcome.surveyorName}, but the text did not send` +
      `${outcome.smsError ? `: ${outcome.smsError}` : ''}. ` +
      'They have not been told. Assign again once texting is working — it will re-send.',
  };
}
